import React, { useMemo } from 'react';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import MuiDialog from '@mui/material/Dialog';
import MuiDialogContent from '@mui/material/DialogContent';
import EDDialogHeader, {
  IProps as IDialogHeaderProps,
} from './DialogHeader/EDDialogHeader';
import EDDialogFooter, {
  IDialogFooterActionButton,
  IProps as IDialogFooterProps,
} from './DialogFooter/EDDialogFooter';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import MuiPaper from '@mui/material/Paper';
import { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import DialogScrollOverlay from '../../Components/DialogScrollOverlay/DialogScrollOverlay';
import EDIcon from '../Icon/EDIcon';
import { ModalProps, Theme } from '@mui/material';
import {
  useIsBreakpointOrSmaller,
  useIsTouchDevice,
} from '../../Components/BreakpointContext/BreakpointContextUtils';
import EDBox from '../EDBox/EDBox';

export interface IDialogProps extends CommonPropsAndTypes.ISizeBehaviorProps {
  open: boolean;
  disableEscapeKeyDown?: boolean;
  title?: string;
  titleIcon?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerTools?: React.ReactNode;
  topToolbarArea?: React.ReactNode;
  headerBottomArea?: React.ReactNode;
  maxWidth?: CommonPropsAndTypes.BreakpointType | false;
  children?: React.ReactNode;
  onClose?: (event: {}) => void;
  fullWidth?: boolean;
  fullHeight?: boolean;
  keepMounted?: boolean;
  onExited?: () => void;
  onEntered?: () => void;
  onEnter?: () => void;
  noCloseIcon?: boolean;
  /** These props will be forwarded to the dialog header */
  headerProps?: IDialogHeaderProps;
  /** The action buttons of the dialog footer */
  footerActions?: IDialogFooterActionButton[];
  /** The content of the dialog footer component */
  footerContent?: React.ReactNode;
  /** These props will be forwarded to the dialog footer */
  footerProps?: IDialogFooterProps;
  fullscreen?: boolean;
  backdropInvisible?: boolean;
  isErrorDialog?: boolean;
  PaperProps?: PaperProps;
  disableRestoreFocus?: boolean;
  disableContentWrapperMargin?: boolean;
  footerResetResourceActions?: false | IDialogFooterActionButton[] | undefined;
  disableHeader?: boolean;
  testId?: string;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  className?: string;
  style?: React.CSSProperties;
  disableDrag?: boolean;
  defaultCursor?: boolean;
}

const styles = (theme: Theme) =>
  ({
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0),
      right: theme.spacing(0),
      height: theme.spacing(3),
    },
    fullHeightPaper: {
      [theme.breakpoints.up('md')]: {
        height: `calc(100% - ${theme.spacing(7)})`,
      },
    },
    fullHeightContentWrapperWithMargin: {
      height: `calc(100% - ${theme.spacing(6)})`,
    },
    fullHeightContentWrapper: {
      height: `calc(100% - 1px)`,
    },
    /** Styles applied to the dialog header element */
    dialogHeader: {
      zIndex: 1,
      boxShadow: theme.customProperties.Shadows.bottomShadow,
    },
    topArea: {},
    bottomArea: {},
    /** Styles applied to the dialog footer element */
    dialogFooter: {},
    /** Styles applied to the dialog content wrapper element */
    contentWrapper: {},
    contentWrapperMargin: {
      margin: theme.spacing(3),
    },
    /** Styles applied to the dialog content component */
    content: {
      padding: theme.spacing(0),
      flexBasis: '100%',
    },

    /** MUI Classes */
    /** Applies to the root element */
    root: {},
    /** Styles applied to the Paper component. */
    paper: {},
    /** Styles applied to the Paper component if scroll="paper" */
    paperScrollPaper: {},
    contentContainerDialogIcon: {
      display: 'flex',
    },
    dialogIcon: {
      marginRight: theme.spacing(2),
    },
    seperatedIconContent: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('sm')]: {
        height: '100%',
      },
      [theme.breakpoints.up('xs')]: {
        height: '100%',
      },
    },
    iconSeparetedContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 0, 2, 3),
    },
  } as const);

const getPaperComponent =
  (disableDrag: boolean) =>
  (
    paperProps: PaperProps & {
      children?: React.ReactNode;
    }
  ) => {
    const nodeRef = React.useRef<HTMLDivElement | null>(null);
    return (
      <Draggable
        disabled={disableDrag}
        cancel=".draggable div *"
        handle=".draggable"
        nodeRef={nodeRef}
      >
        <MuiPaper ref={nodeRef} {...paperProps} />
      </Draggable>
    );
  };

const preventClickPropagation = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
};

/**
Baskomponent för att skapa dialoger
*/
const EDDialogBase: React.FunctionComponent<
  React.PropsWithChildren<IDialogProps>
> = props => {
  const {
    defaultCursor,
    title,
    subtitle,
    headerTools,
    children,
    headerProps,
    topToolbarArea,
    headerBottomArea,
    footerProps,
    footerActions,
    footerContent,
    fullScreenBreakpoint,
    disableResponsiveFullScreen,
    disableContentWrapperMargin,
    onClose,
    noCloseIcon,
    fullHeight,
    backdropInvisible,
    titleIcon,
    isErrorDialog,
    onExited,
    onEntered,
    onEnter,
    footerResetResourceActions,
    disableEscapeKeyDown,
    ...rest
  } = props;
  const classes = withStyles.getClasses(props);
  const {
    contentContainerDialogIcon,
    dialogIcon,
    seperatedIconContent,
    iconSeparetedContainer,
    dialogFooter,
    contentWrapper,
    contentWrapperMargin,
    dialogHeader,
    content,
    closeButton,
    topArea,
    bottomArea,
    fullHeightContentWrapper,
    fullHeightContentWrapperWithMargin,
    paper,
    fullHeightPaper,
    ...muiClasses
  } = classes;
  const isSmallScreen = useIsBreakpointOrSmaller(fullScreenBreakpoint || 'sm');
  const isMediumScreen = useIsBreakpointOrSmaller(fullScreenBreakpoint || 'md');
  const isTouchDevice = useIsTouchDevice();
  const responsiveFullScreen = disableResponsiveFullScreen
    ? false
    : isSmallScreen
    ? true
    : isTouchDevice && isMediumScreen;

  const fullScreen = props.fullscreen || responsiveFullScreen;
  const handleClose: ModalProps['onClose'] = (event, reason): void => {
    if (props.onClose && reason !== 'backdropClick') {
      props.onClose(event);
    }
  };

  const PaperComponent = useMemo(
    () => getPaperComponent(Boolean(props.disableDrag)),
    [props.disableDrag]
  );

  return (
    <MuiDialog
      fullScreen={fullScreen}
      onClose={handleClose}
      disableRestoreFocus={props.disableRestoreFocus}
      PaperComponent={fullScreen ? undefined : PaperComponent}
      onClick={preventClickPropagation}
      BackdropProps={{
        invisible: backdropInvisible,
      }}
      TransitionProps={{ onExited, onEntered, onEnter }}
      data-testid={props.testId ?? props.title ?? ''}
      disableEscapeKeyDown={disableEscapeKeyDown ?? false}
      {...rest}
      classes={{
        ...muiClasses,
        paper: classNames(paper, {
          [fullHeightPaper]: fullHeight,
        }),
      }}
    >
      {!props.disableHeader && (
        <EDDialogHeader
          defaultCursor={defaultCursor}
          title={title}
          titleIcon={props.titleIcon}
          subtitle={subtitle}
          fullScreen={fullScreen}
          tools={
            <div style={{ display: 'flex' }}>
              {headerTools}
              {!noCloseIcon && (
                <EDBox className={closeButton}>
                  <EDIconButton
                    tooltip="Stäng"
                    tooltipInAriaLabelOnly
                    disableTabbing
                    icon="close"
                    iconSize="small"
                    onClick={onClose}
                    color="inherit"
                    padding="dense"
                  />
                </EDBox>
              )}
            </div>
          }
          className={classes.dialogHeader}
          classes={{ topArea: topArea, bottomArea: bottomArea }}
          bottomArea={headerBottomArea}
          topToolbarArea={topToolbarArea}
          {...headerProps}
        />
      )}
      {isErrorDialog ? (
        <div className={classes.seperatedIconContent}>
          <div className={classes.iconSeparetedContainer}>
            <EDIcon iconType={'error'} color={'error'} size="large" />
          </div>

          <MuiDialogContent className={classNames(classes.content)}>
            <div
              className={classNames(classes.contentWrapper, {
                [classes.contentWrapperMargin]:
                  !props.disableContentWrapperMargin,
                [classes.fullHeightContentWrapperWithMargin]:
                  fullHeight && !props.disableContentWrapperMargin,
                [classes.fullHeightContentWrapper]:
                  props.disableContentWrapperMargin,
              })}
            >
              {children}
            </div>
          </MuiDialogContent>
        </div>
      ) : (
        <MuiDialogContent className={classNames(classes.content)}>
          <div
            className={classNames(classes.contentWrapper, {
              [classes.contentWrapperMargin]:
                !props.disableContentWrapperMargin,
              [classes.fullHeightContentWrapperWithMargin]:
                fullHeight && !props.disableContentWrapperMargin,
              [classes.fullHeightContentWrapper]:
                props.disableContentWrapperMargin,
            })}
          >
            {children}
          </div>
        </MuiDialogContent>
      )}
      {(footerActions || footerContent) && (
        <DialogScrollOverlay>
          <EDDialogFooter
            className={classNames(classes.dialogFooter)}
            actions={footerActions}
            content={footerContent}
            footerResetResourceActions={footerResetResourceActions}
            testId={props.testId ?? props.title}
            {...footerProps}
          />
        </DialogScrollOverlay>
      )}
    </MuiDialog>
  );
};

export const EDDialog = withStyles(EDDialogBase, styles);

export default EDDialog;
